import { Injectable } from '@angular/core';

type ProtectedKey =
  | 'authToken'
  | 'jwtToken'
  | 'userToken'
  | 'userDetails'
  | 'hasAiAccess'
  | 'hasSdkAccess'
  | 'hasWebAccess'
  | 'hasArAccess'
  | 'hasCompanionAccess'
  | 'userRole';

interface EncryptedPayload {
  v: 1;
  iv: string;
  data: string;
}

@Injectable({
  providedIn: 'root',
})
export class SecureStorageService {
  private readonly prefix = 'sec:v1:';
  private readonly protectedKeys = new Set<ProtectedKey>([
    'authToken',
    'jwtToken',
    'userToken',
    'userDetails',
    'hasAiAccess',
    'hasSdkAccess',
    'hasWebAccess',
    'hasArAccess',
    'hasCompanionAccess',
    'userRole',
  ]);
  private readonly memoryCache = new Map<string, string>();
  private keyPromise?: Promise<CryptoKey>;

  async init(): Promise<void> {
    await this.getCryptoKey();

    for (const key of this.protectedKeys) {
      const raw = localStorage.getItem(key);
      if (!raw) {
        this.memoryCache.delete(key);
        continue;
      }

      const decrypted = await this.tryDecrypt(raw);
      if (decrypted !== null) {
        this.memoryCache.set(key, decrypted);
        continue;
      }

      this.memoryCache.set(key, raw);
      await this.persistEncrypted(key, raw);
    }
  }

  async setItem(key: string, value: unknown): Promise<void> {
    const serialized = this.serialize(value);
    if (this.isProtectedKey(key)) {
      this.memoryCache.set(key, serialized);
      await this.persistEncrypted(key, serialized);
      return;
    }

    localStorage.setItem(key, serialized);
  }

  getItem<T = string>(key: string): T | null {
    if (this.isProtectedKey(key)) {
      const cached = this.memoryCache.get(key);
      if (cached !== undefined) {
        return this.deserialize<T>(cached);
      }

      const raw = localStorage.getItem(key);
      if (!raw) {
        return null;
      }

      const decrypted = this.decryptSyncFallback(raw);
      if (decrypted !== null) {
        this.memoryCache.set(key, decrypted);
        return this.deserialize<T>(decrypted);
      }

      this.memoryCache.set(key, raw);
      return this.deserialize<T>(raw);
    }

    const value = localStorage.getItem(key);
    return value === null ? null : this.deserialize<T>(value);
  }

  removeItem(key: string): void {
    localStorage.removeItem(key);
    this.memoryCache.delete(key);
  }

  clearProtected(): void {
    for (const key of this.protectedKeys) {
      this.removeItem(key);
    }
  }

  isProtectedKey(key: string): key is ProtectedKey {
    return this.protectedKeys.has(key as ProtectedKey);
  }

  private serialize(value: unknown): string {
    return typeof value === 'string' ? value : JSON.stringify(value);
  }

  private deserialize<T>(value: string): T {
    try {
      return JSON.parse(value) as T;
    } catch {
      return value as T;
    }
  }

  private async persistEncrypted(key: string, plainText: string): Promise<void> {
    const cryptoKey = await this.getCryptoKey();
    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      encoder.encode(plainText)
    );

    const payload: EncryptedPayload = {
      v: 1,
      iv: this.bytesToBase64(iv),
      data: this.bytesToBase64(new Uint8Array(encrypted)),
    };

    localStorage.setItem(key, `${this.prefix}${btoa(JSON.stringify(payload))}`);
  }

  private async tryDecrypt(raw: string): Promise<string | null> {
    try {
      const payload = this.readPayload(raw);
      if (!payload) {
        return null;
      }

      const cryptoKey = await this.getCryptoKey();
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: this.base64ToBytes(payload.iv) },
        cryptoKey,
        this.base64ToBytes(payload.data)
      );

      return new TextDecoder().decode(decrypted);
    } catch {
      return null;
    }
  }

  private decryptSyncFallback(_raw: string): string | null {
    return null;
  }

  private readPayload(raw: string): EncryptedPayload | null {
    if (!raw.startsWith(this.prefix)) {
      return null;
    }

    const encoded = raw.slice(this.prefix.length);
    try {
      const parsed = JSON.parse(atob(encoded)) as EncryptedPayload;
      if (parsed?.v !== 1 || !parsed.iv || !parsed.data) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private async getCryptoKey(): Promise<CryptoKey> {
    if (!this.keyPromise) {
      this.keyPromise = this.createCryptoKey();
    }
    return this.keyPromise;
  }

  private async createCryptoKey(): Promise<CryptoKey> {
    const seed = `${window.location.origin}|digicontests-frontend|secure-storage`;
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(seed));
    return crypto.subtle.importKey('raw', digest, 'AES-GCM', false, ['encrypt', 'decrypt']);
  }

  private bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary);
  }

  private base64ToBytes(value: string): Uint8Array {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
